/**
 * Make `Date#toLocaleString / toLocaleDateString / toLocaleTimeString` render
 * in the RESOLVED zone (user -> org -> detected) instead of the browser zone.
 *
 * Why patch instead of editing call sites: ~125 display sites and most chart
 * tooltips/ticks across ~100 files call these with no `timeZone` option, so
 * each one silently rendered in the laptop's zone even after the user or the
 * organization chose another. Fixing them one by one is a sweep that the next
 * new component would undo; this makes the default correct everywhere at once.
 *
 * Rules:
 *  - An explicit `options.timeZone` is ALWAYS respected (callers that mean UTC
 *    keep meaning UTC).
 *  - Only the default zone changes; locale, fields and format are untouched.
 *  - Invalid dates and failures fall through to the native method unchanged.
 *  - Idempotent; `uninstall` restores the originals (tests).
 *
 * Not covered (and fixed at the call sites instead): code that reads
 * getHours()/getDate()/setHours() — those are browser-local by definition —
 * and `new Date(str)` parsing of zone-less wall-clock input (see zonedTime.js).
 */
import { getCurrentTz } from './timezoneStore';

const MARK = Symbol.for('verops.zonedDateLocale');
const METHODS = ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'];

export const installZonedDateLocale = (getTz = getCurrentTz) => {
  const proto = Date.prototype;
  if (proto[MARK]) return;
  const originals = {};
  METHODS.forEach((name) => {
    const original = proto[name];
    originals[name] = original;
    // eslint-disable-next-line no-extend-native
    Object.defineProperty(proto, name, {
      configurable: true,
      writable: true,
      value: function zonedLocale(locales, options) {
        if (options && options.timeZone) return original.call(this, locales, options);
        let tz;
        try { tz = getTz(); } catch (_) { tz = null; }
        if (!tz || Number.isNaN(this.getTime())) return original.call(this, locales, options);
        try {
          return original.call(this, locales, { ...(options || {}), timeZone: tz });
        } catch (_) {
          return original.call(this, locales, options);
        }
      },
    });
  });
  Object.defineProperty(proto, MARK, { configurable: true, value: originals });
};

export const uninstallZonedDateLocale = () => {
  const proto = Date.prototype;
  const originals = proto[MARK];
  if (!originals) return;
  METHODS.forEach((name) => {
    // eslint-disable-next-line no-extend-native
    Object.defineProperty(proto, name, { configurable: true, writable: true, value: originals[name] });
  });
  delete proto[MARK];
};
